package com.example.positionahead.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.material3.OutlinedButton
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.positionahead.model.*
import com.example.positionahead.ui.theme.gainColor
import com.example.positionahead.ui.theme.lossColor
import com.example.positionahead.util.formatCurrency
import com.example.positionahead.util.formatPercent
import com.example.positionahead.viewmodel.PortfolioUiState

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HomeScreen(
    uiState: PortfolioUiState,
    onNavigateToPortfolio: (String?) -> Unit,
    onNavigateToDiscover: () -> Unit,
    onNavigateToPlan: () -> Unit,
    onNavigateToAdd: () -> Unit,
    modifier: Modifier = Modifier
) {
    val home = uiState.homeState ?: return

    LazyColumn(
        modifier = modifier.fillMaxSize().background(MaterialTheme.colorScheme.background),
        contentPadding = PaddingValues(bottom = 32.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        item {
            HomeHeroSection(home)
        }

        item {
            PaddingWrapper {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    OutlinedButton(onClick = onNavigateToPlan, modifier = Modifier.weight(1f)) {
                        Text("Plan")
                    }
                    OutlinedButton(onClick = onNavigateToAdd, modifier = Modifier.weight(1f)) {
                        Text("Add / import")
                    }
                }
            }
        }

        item {
            PaddingWrapper {
                Text(
                    text = "${home.attentionItems.size} things worth your attention",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Black
                )
            }
        }

        items(home.attentionItems) { item ->
            PaddingWrapper {
                AttentionCard(item, onClick = { onNavigateToPortfolio(item.symbol) })
            }
        }

        item {
            PaddingWrapper {
                Text(
                    text = "Opportunities",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Black,
                    modifier = Modifier.padding(top = 8.dp)
                )
            }
        }

        items(home.opportunities) { opportunity ->
            PaddingWrapper {
                OpportunitySummaryCard(opportunity, onClick = onNavigateToDiscover)
            }
        }

        item {
            PaddingWrapper {
                HealthBriefCard(home.healthScore, onClick = onNavigateToPlan)
            }
        }
    }
}

@Composable
private fun HomeHeroSection(home: HomeState) {
    val color = if (home.dayChange >= 0) gainColor else lossColor
    
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 20.dp, vertical = 24.dp)
    ) {
        Text(
            text = "Today",
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Spacer(modifier = Modifier.height(4.dp))
        Text(
            text = formatCurrency(home.portfolioValue),
            style = MaterialTheme.typography.displayMedium.copy(
                fontSize = 42.sp,
                fontWeight = FontWeight.Black
            )
        )
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = "${if (home.dayChange >= 0) "+" else ""}${formatCurrency(home.dayChange)} (${formatPercent(home.dayChangePercent)})",
                style = MaterialTheme.typography.bodyLarge,
                color = color,
                fontWeight = FontWeight.ExtraBold
            )
        }
    }
}

@Composable
private fun AttentionCard(item: AttentionItem, onClick: () -> Unit) {
    val containerColor = when (item.type) {
        AttentionType.WARNING -> MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.2f)
        AttentionType.SUCCESS -> gainColor.copy(alpha = 0.1f)
        AttentionType.INFO -> MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.2f)
    }
    
    val iconColor = when (item.type) {
        AttentionType.WARNING -> MaterialTheme.colorScheme.error
        AttentionType.SUCCESS -> gainColor
        AttentionType.INFO -> MaterialTheme.colorScheme.primary
    }

    Card(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
        colors = CardDefaults.cardColors(containerColor = containerColor),
        shape = RoundedCornerShape(20.dp),
        elevation = CardDefaults.cardElevation(0.dp)
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Box(
                modifier = Modifier.size(8.dp).clip(RoundedCornerShape(4.dp)).background(iconColor)
            )
            Column {
                Text(item.title, style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.Bold)
                Text(item.detail, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
        }
    }
}

@Composable
private fun OpportunitySummaryCard(recommendation: Recommendation, onClick: () -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        shape = RoundedCornerShape(20.dp),
        border = androidx.compose.foundation.BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.2f))
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Text(recommendation.title, style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.Bold)
            Text(recommendation.detail, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant, maxLines = 2)
        }
    }
}

@Composable
private fun HealthBriefCard(score: Int, onClick: () -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.secondary),
        shape = RoundedCornerShape(20.dp)
    ) {
        Row(
            modifier = Modifier.padding(20.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Column {
                Text("Portfolio Health", color = Color.White.copy(alpha = 0.7f), style = MaterialTheme.typography.labelLarge)
                Text("Your portfolio is solid", color = Color.White, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
            }
            Text("$score", color = Color.White, style = MaterialTheme.typography.displaySmall, fontWeight = FontWeight.Black)
        }
    }
}

@Composable
private fun PaddingWrapper(content: @Composable () -> Unit) {
    Column(modifier = Modifier.padding(horizontal = 20.dp)) {
        content()
    }
}
